import { useNuxtApp } from "#app"; // 导入 `useNuxtApp`
import { getMallAppToken, getPrefixUrl, getToken } from "@/utils/globalData"; // 导入 `useNuxtApp`
import { createEventTrackingContext, generateEventId,REDIRECT_SUB_DOMAIN_STORAGE_KEY,FBCID_STORAGE_KEY,FBCLID_STORAGE_KEY ,isClient} from "@/utils/eventTracking";
import { shouldReportFacebookTracking } from "@/utils/facebookTrackingPolicy";
import { getCurrentLocaleTransferCode, resolveCurrentTransferLanguage } from "@/utils/transferTranslation";
import { resolveSiteTemplate } from "@/templates/_core/getCurrentTemplate";
import { formatJapanPostalCode, normalizeJapanPostalCode } from "@/utils/japanAddress";
import { reportAddCartVisit, resolveOnlineVisitShopId } from "@/utils/onlineVisit";
import { useMainStore } from "../composables/store";
import { makeUnSetupFetch } from "./index";
import {  } from "element-plus/es/utils/browser.mjs";

const FACEBOOK_EVENT_NAMES = {
  login: "Login",
  addToCart: "AddToCart",
  purchase: "Purchase",
};

const TIKTOK_EVENT_NAMES = {
  addToCart: "AddToCart",
  initiateCheckout: "InitiateCheckout",
  completeRegistration: "CompleteRegistration",
  purchase: "Purchase",
};

// 临时关闭 TikTok 后端转化事件上报，待后端接口发布后再打开。
const ENABLE_TIKTOK_SERVER_EVENTS = false;

const HASHED_IDENTIFIER_PATTERN = /^[a-f0-9]{64}$/i;
const CART_LIST_URL = "/mall/admin/mallShoppingCart/list?isShop=1";
const MIXED_CART_LIST_URL = "/mall/admin/mallShoppingCart/mixed/list?isShop=1";

function shouldUseMixedCartList() {
  try {
    const nuxtApp = useNuxtApp();
    const store = useMainStore();
    const currentServer =
      nuxtApp?.$nowCurrentServer?.value ||
      nuxtApp?.$nowCurrentServer?.get?.() ||
      "";
    const storeInfo = currentServer
      ? store.getStoreInfoDataMap(currentServer) || {}
      : {};
    const storePageInfo = storeInfo.storePageInfo || {};
    const siteTemplate = resolveSiteTemplate(store.getSiteInfo(), {
      ...storeInfo,
      ...storePageInfo,
    });

    return siteTemplate.features?.supportsComboProducts === true;
  } catch {
    return false;
  }
}

function withEventTrackingFields(payload = {}) {
  const trackingContext = createEventTrackingContext(payload);

  return {
    ...payload,
    eventId: trackingContext.eventId,
    eventSourceUrl: trackingContext.eventSourceUrl,
  };
}

function resolveEventTime(eventTime) {
  const normalizedEventTime = Number(eventTime);

  if (Number.isFinite(normalizedEventTime) && normalizedEventTime > 0) {
    return Math.floor(normalizedEventTime);
  }

  return Math.floor(Date.now() / 1000);
}

function normalizeEventStringValue(value) {
  if (value === undefined || value === null) {
    return "";
  }

  return `${value}`.trim();
}

async function sha256Hex(value) {
  const normalizedValue = normalizeEventStringValue(value);
  if (!normalizedValue) {
    return "";
  }

  if (HASHED_IDENTIFIER_PATTERN.test(normalizedValue)) {
    return normalizedValue.toLowerCase();
  }

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(normalizedValue);
    const subtle = globalThis?.crypto?.subtle;
    if (!subtle) {
      return normalizedValue;
    }
    const digest = await subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch (error) {
    return normalizedValue;
  }
}

function normalizePhoneHashInput(value) {
  const normalizedValue = normalizeEventStringValue(value);

  if (!normalizedValue) {
    return "";
  }

  return normalizedValue.replace(/(?!^\+)[^\d]/g, "");
}

function getTikTokEventOptType(eventName) {
  const optTypeMap = {
    [TIKTOK_EVENT_NAMES.addToCart]: "add_to_cart",
    [TIKTOK_EVENT_NAMES.initiateCheckout]: "initiate_checkout",
    [TIKTOK_EVENT_NAMES.completeRegistration]: "complete_registration",
    [TIKTOK_EVENT_NAMES.purchase]: "purchase",
  };

  return optTypeMap[eventName] || normalizeEventStringValue(eventName).toLowerCase();
}

function normalizeTikTokCustomData(customData = {}) {
  if (!customData || typeof customData !== "object" || Array.isArray(customData)) {
    return undefined;
  }

  const normalizedCustomData = {};

  if (customData.value !== undefined && customData.value !== null && `${customData.value}`.trim() !== "") {
    normalizedCustomData.value = String(customData.value);
  }

  if (customData.currency !== undefined && customData.currency !== null && `${customData.currency}`.trim() !== "") {
    normalizedCustomData.currency = String(customData.currency);
  }

  const contentIds = Array.isArray(customData.contentIds)
    ? customData.contentIds
        .map((item) => normalizeEventStringValue(item))
        .filter(Boolean)
    : [];

  if (contentIds.length > 0) {
    normalizedCustomData.contentIds = contentIds;
  }

  const contentType = normalizeEventStringValue(customData.contentType);
  if (contentType) {
    normalizedCustomData.contentType = contentType;
  }

  const contentName = normalizeEventStringValue(customData.contentName);
  if (contentName) {
    normalizedCustomData.contentName = contentName;
  }

  return Object.keys(normalizedCustomData).length > 0 ? normalizedCustomData : undefined;
}

function getDefaultTikTokUserData(overrides = {}) {
  let storeUserInfo = {};

  try {
    storeUserInfo = useMainStore()?.userInfo || {};
  } catch (error) {
    storeUserInfo = {};
  }

  return {
    clientIpAddress: normalizeEventStringValue(overrides.clientIpAddress),
    clientUserAgent:
      normalizeEventStringValue(overrides.clientUserAgent) ||
      (process.client && typeof navigator !== "undefined" ? navigator.userAgent : ""),
    externalId:
      overrides.externalId ??
      storeUserInfo.id ??
      storeUserInfo.memberCode ??
      "",
    email: overrides.email ?? storeUserInfo.email ?? "",
    phone:
      overrides.phone ??
      storeUserInfo.mobile ??
      storeUserInfo.mobileNo ??
      storeUserInfo.phone ??
      storeUserInfo.phoneNo ??
      "",
  };
}

async function normalizeTikTokUserData(userData = {}) {
  if (!userData || typeof userData !== "object" || Array.isArray(userData)) {
    return undefined;
  }

  const resolvedUserData = getDefaultTikTokUserData(userData);
  const normalizedUserData = {};

  const clientIpAddress = normalizeEventStringValue(resolvedUserData.clientIpAddress);
  if (clientIpAddress) {
    normalizedUserData.clientIpAddress = clientIpAddress;
  }

  const clientUserAgent = normalizeEventStringValue(resolvedUserData.clientUserAgent);
  if (clientUserAgent) {
    normalizedUserData.clientUserAgent = clientUserAgent;
  }

  const externalId = await sha256Hex(normalizeEventStringValue(resolvedUserData.externalId));
  if (externalId) {
    normalizedUserData.externalId = externalId;
  }

  const email = await sha256Hex(normalizeEventStringValue(resolvedUserData.email).toLowerCase());
  if (email) {
    normalizedUserData.email = email;
  }

  const phone = await sha256Hex(normalizePhoneHashInput(resolvedUserData.phone));
  if (phone) {
    normalizedUserData.phone = phone;
  }

  return Object.keys(normalizedUserData).length > 0 ? normalizedUserData : undefined;
}

async function normalizeTikTokEvent(event = {}) {
  const eventName = normalizeEventStringValue(event.eventName);

  if (!eventName) {
    return null;
  }

  const normalizedEvent = {
    eventId: event.eventId || generateEventId(),
    eventSourceUrl: event.eventSourceUrl || (process.client && typeof window !== "undefined" ? window.location.href : ""),
    actionSource: event.actionSource || "website",
    eventTime: resolveEventTime(event.eventTime),
    eventName,
    optType: normalizeEventStringValue(event.optType) || getTikTokEventOptType(eventName),
  };

  const customData = normalizeTikTokCustomData(event.customData);
  if (customData) {
    normalizedEvent.customData = customData;
  }

  const userData = await normalizeTikTokUserData(event.userData);
  if (userData) {
    normalizedEvent.userData = userData;
  }

  const numericUserId = Number(event.userId);
  if (Number.isFinite(numericUserId) && numericUserId > 0) {
    normalizedEvent.userId = numericUserId;
  } else {
    let defaultUserId = NaN;
    try {
      defaultUserId = Number(useMainStore()?.userInfo?.id);
    } catch (error) {
      defaultUserId = NaN;
    }
    if (Number.isFinite(defaultUserId) && defaultUserId > 0) {
      normalizedEvent.userId = defaultUserId;
    }
  }

  const testEventCode = normalizeEventStringValue(event.testEventCode);
  if (testEventCode) {
    normalizedEvent.testEventCode = testEventCode;
  }

  return normalizedEvent;
}

function normalizeFacebookCustomData(customData = {}) {
  if (!customData || typeof customData !== "object" || Array.isArray(customData)) {
    return undefined;
  }

  const normalizedCustomData = Object.entries(customData).reduce((result, [key, value]) => {
    if (value === undefined || value === null) {
      return result;
    }

    result[key] = value;
    return result;
  }, {});

  if (normalizedCustomData.value !== undefined) {
    normalizedCustomData.value = String(normalizedCustomData.value);
  }

  if (normalizedCustomData.currency !== undefined) {
    normalizedCustomData.currency = String(normalizedCustomData.currency);
  }

  if (normalizedCustomData.contentType !== undefined) {
    normalizedCustomData.contentType = String(normalizedCustomData.contentType);
  }

  if (Array.isArray(normalizedCustomData.contentIds)) {
    normalizedCustomData.contentIds = normalizedCustomData.contentIds
      .filter((item) => item !== undefined && item !== null && `${item}`.trim() !== "")
      .map((item) => String(item));

    if (normalizedCustomData.contentIds.length === 0) {
      delete normalizedCustomData.contentIds;
    }
  }

  return Object.keys(normalizedCustomData).length > 0 ? normalizedCustomData : undefined;
}

export function fixFbParams(locationHref){ // 补齐fb参数
  if(!isClient()){
    return locationHref;
  }
  let joinStr=""
  if(locationHref.indexOf("?")<0){
    joinStr="?"
  }
  if(locationHref.indexOf(REDIRECT_SUB_DOMAIN_STORAGE_KEY)<0&&localStorage.getItem(REDIRECT_SUB_DOMAIN_STORAGE_KEY)){
    joinStr+="&"+REDIRECT_SUB_DOMAIN_STORAGE_KEY+"="+localStorage.getItem(REDIRECT_SUB_DOMAIN_STORAGE_KEY);
  }
  if(locationHref.indexOf(FBCID_STORAGE_KEY)<0&&localStorage.getItem(FBCID_STORAGE_KEY)){
    joinStr+="&"+FBCID_STORAGE_KEY+"="+localStorage.getItem(FBCID_STORAGE_KEY);
  }
  if(locationHref.indexOf(FBCLID_STORAGE_KEY)<0&&localStorage.getItem(FBCLID_STORAGE_KEY)){
    joinStr+="&"+FBCLID_STORAGE_KEY+"="+localStorage.getItem(FBCLID_STORAGE_KEY);
  }
  return (locationHref+joinStr).replace(/\&\&/g,'&').replace(/\?\&/g,'?')
}

function normalizeFacebookEvent(event = {}) {
  const eventName = `${event.eventName || ""}`.trim();

  if (!eventName) {
    return null;
  }

  const normalizedEvent = {
    eventId: event.eventId || generateEventId(),
    eventSourceUrl: process.client && typeof window !== "undefined" ? fixFbParams(window.location.href) : "",
    actionSource: event.actionSource || "website",
    eventTime: resolveEventTime(event.eventTime),
    eventName,
  };
  const customData = normalizeFacebookCustomData(event.customData);

  if (customData) {
    normalizedEvent.customData = customData;
  }

  return normalizedEvent;
}

function getFacebookEventRequestToken() {
  if (process.client) {
    return getToken() || getMallAppToken() || "";
  }

  try {
    const headers = useRequestHeaders(["authorization"]);
    return headers?.authorization || "";
  } catch (error) {
    return "";
  }
}

function getFacebookEventRequestShopId() {
  try {
    const dealerShopIdCookie = useCookie("dealerShopId", { path: "/" });
    return dealerShopIdCookie?.value || "";
  } catch (error) {
    if (!process.server) {
      return "";
    }

    try {
      const headers = useRequestHeaders(["cookie"]);
      const match = headers?.cookie?.match(/(?:^|;\s*)dealerShopId=([^;]+)/);

      if (match?.[1]) {
        return decodeURIComponent(match[1]);
      }
    } catch (_) {}

    return "";
  }
}

export function shouldReportFacebookForCurrentShop() {
  try {
    const siteInfo = useMainStore()?.getSiteInfo?.();
    return shouldReportFacebookTracking(siteInfo?.configData);
  } catch (error) {
    // Preserve the existing reporting behavior if the shop configuration is unavailable.
    return true;
  }
}

export function sendFacebookEvents(events = []) {
  if (!shouldReportFacebookForCurrentShop()) {
    return Promise.resolve(null);
  }

  const normalizedEvents = events
    .map((event) => normalizeFacebookEvent(event))
    .filter(Boolean);
  const shopId = getFacebookEventRequestShopId();
  const authorization = getFacebookEventRequestToken();

  if (normalizedEvents.length === 0) {
    return Promise.resolve(null);
  }

  return useNuxtApp().$customFetch("/mall/api/mall/app/facebook/sendEvents", {
    method: "POST",
    credentials: "include",
    headers: {
      authorization,
      shopId,
    },
    body: {
      param: {
        shopId,
        data: normalizedEvents,
      },
    },
  });
}

export function sendFacebookEvent(event = {}) {
  return sendFacebookEvents([event]);
}

export function buildFacebookEventsPayload(events = []) {
  if (!shouldReportFacebookForCurrentShop()) {
    return null;
  }

  const normalizedEvents = events
    .map((event) => normalizeFacebookEvent(event))
    .filter(Boolean);
  const shopId = getFacebookEventRequestShopId();
  const authorization = getFacebookEventRequestToken();

  if (normalizedEvents.length === 0) {
    return null;
  }

  return {
    headers: {
      authorization,
      shopId,
    },
    body: {
      param: {
        shopId,
        data: normalizedEvents,
      },
    },
  };
}

export function buildFacebookEventPayload(event = {}) {
  return buildFacebookEventsPayload([event]);
}

export async function sendFacebookEventSafely(event = {}) {
  try {
    return await sendFacebookEvent(event);
  } catch (error) {
    console.warn("facebook sendEvents failed:", error);
    return null;
  }
}

export async function sendTikTokEvents(events = []) {
  if (!ENABLE_TIKTOK_SERVER_EVENTS) {
    return null;
  }

  const normalizedEvents = (await Promise.all(
    events.map((event) => normalizeTikTokEvent(event))
  )).filter(Boolean);
  const shopId = getFacebookEventRequestShopId();
  const authorization = getFacebookEventRequestToken();

  if (normalizedEvents.length === 0) {
    return null;
  }

  return useNuxtApp().$customFetch("/mall/api/mall/app/tiktok/sendEvents", {
    method: "POST",
    headers: {
      authorization,
      shopId,
    },
    body: {
      param: {
        shopId,
        data: normalizedEvents,
      },
    },
  });
}

export function sendTikTokEvent(event = {}) {
  return sendTikTokEvents([event]);
}

export async function sendTikTokEventSafely(event = {}) {
  try {
    return await sendTikTokEvent(event);
  } catch (error) {
    console.warn("tiktok sendEvents failed:", error);
    return null;
  }
}
/**
 * 获取当前用户信息
 * @param params
 * @returns {*}
 */
export function getCurrentUserData(body, $fetch, headers) {
  return useNuxtApp().$customFetch("/mall/portal/user/query?isShop=1", {
    method: "POST",
    body: {
      ...body,
      param: {
        account: window.localStorage.getItem("lastLoginAccount"),
      },
    },
  });
}

/**
 * 获取当前商城用户信息
 * @param params
 * @returns {*}
 */
export function getCurrentMallUserData(body, $fetch, headers) {
  return useNuxtApp().$customFetch(
    "/mallSite/portal/auth/loginTourists?mall=1",
    { method: "POST", body }
  );
}
/**
 * 翻译数据
 * @param params
 * @returns {*}
 */
export function alibabaProductTextTranslate(body, $fetch, headers) {
  return useNuxtApp().$customFetch(
    "mallSite/admin/alibabaGoods/alibabaProductTextTranslate?mall=1&uncheck401=1",
    { method: "POST", body }
  );
}

/**
 * 批量翻译数据
 * @param body
 * @returns {*}
 */
export function batchTranslateTexts(body, authorization = "", $fetch, headers) {
  const targetLanguage = getCurrentLocaleTransferCode(body?.targetLanguage);
  const normalizedBody = {
    isSystemText: typeof body?.isSystemText === "number" ? body.isSystemText : 0,
    texts: Array.isArray(body?.texts) ? body.texts : [],
    sourceLanguage: body?.sourceLanguage || "zh",
    targetLanguage,
    appType: body?.appType || "react-mall-app",
  };

  const requestHeaders = {
    authorization: authorization || getMallAppToken() || getToken() || "",
  };

  return useNuxtApp().$customFetch(
    "/frontServer/translate/batch?hiddenErrTip=1&getCache=1",
    {
      method: "POST",
      body: normalizedBody,
      headers: requestHeaders,
    }
  );
}

/**
 * 获取当前汇率 币种
 * @param params
 * @returns {*}
 */

export function getCurrencyDataList(body, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/dealerShop/queryAllCurrencyRate?isShop=1",
      { method: "POST", body }
    );
  }

  return useNuxtApp().$customFetch(
    "/mall/admin/dealerShop/queryAllCurrencyRate?isShop=1",
    { method: "POST", body }
  );
}

/**
 * 获取店铺默认币种
 * @param params
 * @returns {*}
 */
export function queryDefaultCurrencyCode(body, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/dealerShop/queryDefaultCurrencyCode?isShop=1",
      { method: "POST", body }
    );
  }

  return useNuxtApp().$customFetch(
    "/mall/admin/dealerShop/queryDefaultCurrencyCode?isShop=1",
    { method: "POST", body }
  );
}

/**
 * 查询指定币种汇率
 * @param params
 * @returns {*}
 */
export function queryCurrencyRate(body, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/dealerShop/queryCurrencyRate?isShop=1",
      { method: "POST", body }
    );
  }

  return useNuxtApp().$customFetch(
    "/mall/admin/dealerShop/queryCurrencyRate?isShop=1",
    { method: "POST", body }
  );
}

 
/**
 * 邮箱登录
 * @param params
 * @returns {*}
 */
export function emailLogin(body) {
  const trackedBody = withEventTrackingFields(body || {});

  return useNuxtApp().$customFetch("/mall/portal/auth/loginWithEmailVerificationCode?isShop=1", {
    method: "POST",
    body:{
      ...trackedBody,
      eventId: trackedBody.eventId,
      eventSourceUrl: process.client?window.location.href:trackedBody.eventSourceUrl,
      
      shopId: (process.client ?useCookie('dealerShopId').value : "")
    }
  }).then((response) => {
    if (response?.success) {
      void sendFacebookEventSafely({
        eventId: trackedBody.eventId,
        eventSourceUrl: trackedBody.eventSourceUrl,
        eventName: FACEBOOK_EVENT_NAMES.login,
      });
    }

    return response;
  });
}

 
/**
 * 邮箱登录
 * @param params
 * @returns {*}
 */
export function getEmailLoginCode(body) {
  return useNuxtApp().$customFetch("/mall/portal/auth/sendEmailVerificationCode?isShop=1", {
    method: "POST",
    body:{
      ...(body||{}),
      shopId: (process.client ?useCookie('dealerShopId').value : "")
    }
  });
}

/**
 * 登录
 * @param params
 * @returns {*}
 */
export function login(body) {
  const trackedBody = withEventTrackingFields(body || {});

  return useNuxtApp().$customFetch("/mall/portal/auth/login?isShop=1", {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    method: "POST",
    body: (() => {
      const form = new URLSearchParams();
      if (trackedBody && typeof trackedBody === "object") {
        Object.entries(trackedBody).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            form.append(key, String(value));
          }
        });
      }
      return form;
    })(),
  }).then((response) => {
    if (response?.success) {
      void sendFacebookEventSafely({
        eventId: trackedBody.eventId,
        eventSourceUrl: trackedBody.eventSourceUrl,
        eventName: FACEBOOK_EVENT_NAMES.login,
      });
    }

    return response;
  });
}

/**
 * 获取会员等级/权益列表
 * @param params
 * @returns {*}
 */
export function getMemberLevelList(body = {}) {
  const innerParam = {
    pageNo: 1,
    pageSize: 10,
    ...(body.param || {})
  }
  return useNuxtApp().$customFetch("/mall/admin/memberLevel/memberLevelList?isShop=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: innerParam
    }
  })
}

/**
 * 分页查询用户优惠券
 * @param {Object} body
 * @returns {Promise}
 */
export function queryUserCouponPage(body = {}) {
  const innerParam = {
    pageNo: 0,
    pageSize: 20,
    ...(body.param || {})
  }
  return useNuxtApp().$customFetch("/mall/admin/userCoupon/queryPage?isShop=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: innerParam
    }
  })
}

/**
 * 查询用户优惠券详情
 * @param {Object} body
 * @returns {Promise}
 */
export function queryUserCouponById(body = {}) {
  return useNuxtApp().$customFetch("/mall/admin/userCoupon/queryById?isShop=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: {
        ...(body.param || {})
      }
    }
  })
}

/**
 * 选择最优优惠券组合
 * @param {Object} body
 * @returns {Promise}
 */
export function selectBestCoupons(body = {}) {
  return useNuxtApp().$customFetch("/mall/admin/userCoupon/selectBestCoupons?isShop=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: {
        orderAmount: body?.param?.orderAmount || 0,
        ...(body.param || {})
      }
    }
  })
}

/**
 * 查看下一档次优惠券信息
 * @param {Object} body
 * @returns {Promise}
 */
export function nextLevelCoupon(body = {}) {
  return useNuxtApp().$customFetch("/mall/admin/userCoupon/nextLevelCoupon?isShop=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: {
        orderAmount: body?.param?.orderAmount || 0,
        ...(body.param || {})
      }
    }
  })
}

/**
 * 购物车/商品详情满额礼包凑单试算。
 * 请求体保持商城接口统一的 { param: {...} } 结构。
 * @param {Object} body
 * @returns {Promise}
 */
export function calculateFullAmountGiftAddOn(body = {}) {
  return useNuxtApp().$customFetch("/mall/admin/fullAmountGift/calculateAddOn?isShop=1&uncheck401=1&hiddenErrTip=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: {
        ...(body.param || {}),
        skuList: Array.isArray(body?.param?.skuList) ? body.param.skuList : [],
      },
    },
  })
}

/**
 * 根据一个或多个主商品 SPU 查询关联推荐商品。
 * @param {Object} body
 * @returns {Promise}
 */
export function queryRelatedSpuItemList(body = {}) {
  return useNuxtApp().$customFetch("/mall/admin/mallGoods/queryRelatedSpuItemList?isShop=1&uncheck401=1&hiddenErrTip=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: {
        pageNo: 0,
        pageSize: 20,
        ...(body.param || {}),
        relationSourceSpuIdList: Array.isArray(body?.param?.relationSourceSpuIdList)
          ? body.param.relationSourceSpuIdList
          : [],
      },
    },
  })
}

/**
 * 分页查询积分记录
 * @param {Object} body
 * @returns {Promise}
 */
export function queryPointsRecordPage(body = {}) {
  const innerParam = {
    pageNo: 0,
    pageSize: 10,
    ...(body.param || {})
  }
  return useNuxtApp().$customFetch("/mall/admin/pointsRecord/queryPage?isShop=1", {
    method: "POST",
    body: {
      ...(body || {}),
      param: innerParam
    }
  })
}

/**
 * 验证码
 * @param params
 * @returns {*}
 */
export function validateSmsCode(body) {
  return useNuxtApp().$customFetch(
    "/mall/portal/auth/validateSmsCode?isShop=1",
    {
      method: "POST",
      body,
    }
  );
}

/**
 * 注册请求
 * @param params
 * @returns {*}
 */
export function regist(body) {
  return useNuxtApp().$customFetch("/mall/portal/auth/regist?isShop=1", {
    method: "POST",
    body,
  });
}

/**
 * 图片验证码
 * @param params
 * @returns {*}
 */
export function getImgCode(body) {
  return useNuxtApp().$customFetch("/mall/portal/auth/getImgCode?isShop=1", {
    method: "POST",
    body,
  });
}

/**
 * 验证图片验证码
 * @param params
 * @returns {*}
 */
export function validateImgCode(body) {
  return useNuxtApp().$customFetch(
    "/mall/portal/auth/validateImgCode?isShop=1",
    {
      method: "POST",
      body,
    }
  );
}

/**
 * 发送短信验证码
 * @param params
 * @returns {*}
 */
export function sendSmsCode(body) {
  return useNuxtApp().$customFetch("/mall/portal/auth/sendSmsCode?isShop=1", {
    method: "POST",
    body,
  });
}

/**
 * 通过邮件找回密码
 * @param params
 * @returns {*}
 */
export function resetAndEmail(body) {
  return useNuxtApp().$customFetch(
    "/mall/portal/user/pwd/resetAndEmail?isShop=1",
    {
      method: "POST",
      body,
    }
  );
}

/**
 * 添加商品到购物车
 * @param {Object} param - 参数对象
 * @param {number} param.buyAmount - 购买数量
 * @param {string} param.specsInfo - 规格信息
 * @param {string} param.dealerItemSkuId - SKU ID
 * @param {string} param.dealerShopLiveStreamerId - 直播主播ID（可选）
 * @returns {Promise}
 */
export function addToCart(param) {
  const nuxtApp = useNuxtApp();
  const trackedParam = withEventTrackingFields(param || {});
  const addCartVisitOptions = {
    nuxtApp,
    shopId: resolveOnlineVisitShopId({
      nuxtApp,
      shopId: trackedParam.dealerShopId || trackedParam.shopId,
    }),
  };
  const facebookEventCustomData = {
    currency: "TWD",
    contentIds: trackedParam.dealerItemSkuId ? [trackedParam.dealerItemSkuId] : [],
    contentType: "product",
    ...(trackedParam.facebookEventCustomData || {}),
  };
  const tikTokEventCustomData = {
    currency: "TWD",
    contentIds: trackedParam.dealerItemSkuId ? [trackedParam.dealerItemSkuId] : [],
    contentType: "product",
    ...(trackedParam.tikTokEventCustomData || {}),
  };

  return nuxtApp.$customFetch(
    "/mall/admin/mallShoppingCart/add?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          buyAmount: trackedParam.buyAmount || 1,
          specsInfo: trackedParam.specsInfo || "",
          dealerItemSkuId: trackedParam.dealerItemSkuId,
          dealerShopLiveStreamerId: trackedParam.dealerShopLiveStreamerId || "",
          eventId: trackedParam.eventId,
          eventSourceUrl:process.client?window.location.href: trackedParam.eventSourceUrl,
        },
      },
    }
  ).then((response) => {
    if (response?.success) {
      void reportAddCartVisit(addCartVisitOptions);
      void sendFacebookEventSafely({
        eventId: trackedParam.eventId,
        eventSourceUrl: trackedParam.eventSourceUrl,
        eventName: FACEBOOK_EVENT_NAMES.addToCart,
        customData: facebookEventCustomData,
      });
      void sendTikTokEventSafely({
        eventId: trackedParam.eventId,
        eventSourceUrl: trackedParam.eventSourceUrl,
        eventName: TIKTOK_EVENT_NAMES.addToCart,
        customData: tikTokEventCustomData,
        userData: trackedParam.tikTokEventUserData,
      });
    }

    return response;
  });
}

/**
 * 获取购物车列表
 * @param {Object} param - 参数对象
 * @param {number} param.pageNo - 页码，从0开始
 * @param {number} param.pageSize - 每页大小
 * @returns {Promise}
 */
export function getCartList(param = {}) {
  const useMixedCartList = shouldUseMixedCartList();
  const requestUrl = useMixedCartList
    ? MIXED_CART_LIST_URL
    : CART_LIST_URL;
  const requestParam = {
    pageNo: param.pageNo || 0,
    pageSize: param.pageSize || 100000,
  };

  if (useMixedCartList) {
    requestParam.itemType = param.itemType ?? null;
  }

  return useNuxtApp().$customFetch(
    requestUrl,
    {
      method: "POST",
      body: {
        param: requestParam,
      },
    }
  );
}

/**
 * 删除购物车商品
 * @param {Object} param - 参数对象
 * @param {string} param.id - 购物车商品ID
 * @returns {Promise}
 */
export function removeFromCart(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallShoppingCart/delete?isShop=1",
    {
      method: "POST",
      body: {
        param: [{  dealerItemSkuId: param.id}]
      }
    }
  );
}

/**
 * 更新购物车商品数量
 * @param {Object} param - 参数对象
 * @param {string} param.id - 购物车商品ID
 * @param {number} param.buyAmount - 购买数量
 * @returns {Promise}
 */
export function updateCartQuantity(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallShoppingCart/update?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          id: param.id,
          buyAmount: param.buyAmount,
        },
      },
    }
  );
}

/**
 * 修改购物车商品数量
 * @param {Object} param - 参数对象
 * @param {string} param.dealerItemSkuId - SKU ID
 * @param {number} param.buyAmount - 购买数量
 * @param {number} [param.timeout] - 请求超时时间（毫秒）
 * @returns {Promise}
 */
export function modifyCartQuantity(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallShoppingCart/modify?isShop=1",
    {
      method: "POST",
      ...(Number(param.timeout) > 0 ? { timeout: Number(param.timeout) } : {}),

      body: {
        param: {
          dealerItemSkuId: param.dealerItemSkuId,
          buyAmount: param.buyAmount,
        },
      },
    }
  );
}

// export function confirmOrder(param) {
//   return useNuxtApp().$customFetch(
//     "/mall/admin/mallOrder/confirmOrder?isShop=1",
//     {
//       method: "POST",
//       body: {
//         param: {
//           confirmOrderSkuReqDTOList: param.confirmOrderSkuReqDTOList,
//         },
//       },
//     }
//   );
// }
export function confirmOrder(param) {
  const transferLanguage = resolveCurrentTransferLanguage("en");
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/confirmOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          countryCode: param.countryCode || "CN",
          confirmOrderSkuReqDTOList: param.confirmOrderSkuReqDTOList,
        },
        ___transferConfig: {
          language: transferLanguage,
          setPath: "data.transfersMap",
          transferType: "text",
          transferKeysConfig: JSON.stringify([
            ["data.shippingFeePlanName"],
            ["data.shippingPlanName"],
          ]),
        },
      },
    }
  );
}

/**
 * 日本店铺确认订单。
 * 日本模板使用非拆单确认接口，避免成功页商品数量和金额与购物车不一致。
 */
export function confirmJapanOrder(param) {
  const transferLanguage = resolveCurrentTransferLanguage("en");
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/confirmOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          countryCode: param.countryCode || "JP",
          confirmOrderSkuReqDTOList: param.confirmOrderSkuReqDTOList,
        },
        ___transferConfig: {
          language: transferLanguage,
          setPath: "data.transfersMap",
          transferType: "text",
          transferKeysConfig: JSON.stringify([
            ["data.shippingFeePlanName"],
            ["data.shippingPlanName"],
          ]),
        },
      },
    }
  );
}

/**
 * 获取商品详情 - 用于购物车结算
 * @param {Object} param - 参数对象
 * @param {string} param.id - 商品ID
 * @param {string} param.spItemSpuId - SPU ID
 * @returns {Promise} API响应
 */
export function getItemSpuDetail(param) {

  if (!param.dealerShopId) {
    try {
      const dealerShopIdCookie = useCookie("dealerShopId", { path: "/" });
      if (dealerShopIdCookie && dealerShopIdCookie.value) {
        param.dealerShopId = dealerShopIdCookie.value;
      }
    } catch (e) {
      // SSR fallback: parse from incoming request headers
      if (headers && headers.cookie && typeof headers.cookie === "string") {
        const match = headers.cookie.match(/(?:^|;\s*)dealerShopId=([^;]+)/);
        if (match && match[1]) {
          try {
            param.dealerShopId = decodeURIComponent(match[1]);
          } catch (_) {}
        }
      }
    }
  }
  return useNuxtApp().$customFetch(
    "/mall/admin/mallGoods/itemSpuDetail?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          dealerShopId:param.dealerShopId,
          id: param.id,
          spItemSpuId: param.spItemSpuId
        }
      }
    }
  );
}

/**
 * 立即购买确认订单 - 直接购买接口
 * @param {Object} param - 参数对象
 * @param {Array} param.confirmOrderSkuReqDTOList - 订单商品列表
 * @returns {Promise} API响应
 */
export function buyNowConfirmOrder(param) {
  const transferLanguage = resolveCurrentTransferLanguage("en");
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/confirmOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          countryCode: param.countryCode || "CN",
          confirmOrderSkuReqDTOList: param.confirmOrderSkuReqDTOList
        },
        ___transferConfig: {
          language: transferLanguage,
          setPath: "data.transfersMap",
          transferType: "text",
          transferKeysConfig: JSON.stringify([
            ["data.shippingFeePlanName"],
            ["data.shippingPlanName"],
          ]),
        },
      }
    }
  );
}

function normalizeAddressItem(item = {}) {
  if (!item || typeof item !== "object") {
    return item;
  }

  const zipCode = item.zipCode || item.postalCode || "";
  const detailAddress = item.detailAddress || item.address || "";

  return {
    ...item,
    id: item.id || item.addressId || "",
    addressId: item.addressId || item.id || "",
    countryCode: item.countryCode || (item.country === "日本" ? "JP" : ""),
    zipCode,
    postalCode: item.postalCode || zipCode,
    detailAddress,
    address: item.address || detailAddress,
  };
}

function normalizeAddressResponse(response) {
  if (!response || typeof response !== "object") {
    return response;
  }

  if (Array.isArray(response.data)) {
    return {
      ...response,
      data: response.data.map((item) => normalizeAddressItem(item)),
    };
  }

  if (response.data && typeof response.data === "object") {
    return {
      ...response,
      data: normalizeAddressItem(response.data),
    };
  }

  return response;
}

function isJapanAddressPayload(param = {}, fallbackCountryCode = "") {
  const countryCode = String(
    param.countryCode || fallbackCountryCode || ""
  ).toUpperCase();
  const country = String(param.country || "").trim();

  return countryCode === "JP" || country === "日本" || country.toLowerCase() === "japan";
}

function formatPostalCodeForAddressPayload(param = {}, fallbackCountryCode = "") {
  const rawPostalCode = param.zipCode || param.postalCode || "";

  if (isJapanAddressPayload(param, fallbackCountryCode)) {
    const digits = normalizeJapanPostalCode(rawPostalCode);
    return digits.length > 3 ? formatJapanPostalCode(digits) : digits;
  }

  return String(rawPostalCode).replace(/\D/g, "");
}

function buildAddressManagePayload(param = {}) {
  const sendType = param.sendType ?? 0;
  const country = param.country || "";
  const countryCode = param.countryCode || (country === "日本" ? "JP" : "");
  const zipCode = formatPostalCodeForAddressPayload(param, countryCode);

  return {
    id: param.id || param.addressId || "",
    addressId: param.addressId || param.id || "",
    countryCode,
    country,
    lineId: param.lineId || "",
    userRealname: param.userRealname || "",
    userMobile: param.userMobile || param.mobileNo || "",
    zipCode,
    postalCode: zipCode,
    province: param.province || "",
    city: param.city || "",
    district: param.district || "",
    street: param.street || "",
    detailAddress: param.detailAddress || param.address || "",
    defaultState: param.defaultState ?? 0,
    sendType,
    siteAddress: param.siteAddress || "",
    siteCode: param.siteCode || "",
    siteName: param.siteName || "",
    siteType: param.siteType ?? (sendType === 1 ? "1" : ""),
    siteTypeName: param.siteTypeName || "",
  };
}

function normalizeCustomerAddressDTO(param = {}) {
  if (!param || typeof param !== "object") {
    return {};
  }

  const country = param.country || "";
  const countryCode = param.countryCode || (country === "日本" ? "JP" : "");
  const zipCode = formatPostalCodeForAddressPayload(param, countryCode);
  const userMobile = param.userMobile || param.mobileNo || "";

  return {
    ...param,
    countryCode,
    country,
    zipCode,
    postalCode: zipCode,
    userRealname: param.userRealname || "",
    userMobile,
    mobileNo: userMobile,
    province: param.province || "",
    city: param.city || "",
    district: param.district || "",
    street: param.street || "",
    detailAddress: param.detailAddress || param.address || "",
    sendType: param.sendType ?? 0,
  };
}

/**
 * 获取默认地址
 * @returns {Promise}
 */
export function getDefaultAddress() {
  return useNuxtApp().$customFetch(
    "/mall/admin/addressManage/getDefault?isShop=1",
    {
      method: "POST",
      body: {
        param: {},
      },
    }
  ).then((response) => normalizeAddressResponse(response));
}

/**
 * 获取地址列表
 * @param {Object} param - 参数对象
 * @param {number} param.pageNo - 页码，从0开始
 * @param {number} param.pageSize - 每页大小
 * @returns {Promise}
 */
export function getAddressList(param = {}) {
  return useNuxtApp().$customFetch("/mall/admin/addressManage/list?isShop=1", {
    method: "POST",
    body: {
      param: {
        pageNo: param.pageNo || 0,
        pageSize: param.pageSize || 20,
      },
    },
  }).then((response) => normalizeAddressResponse(response));
}

/**
 * 新增地址
 * @param {Object} param - 地址参数对象
 * @param {string} param.userRealname - 收货人姓名
 * @param {string} param.userMobile - 手机号码
 * @param {string} param.province - 省份
 * @param {string} param.city - 城市
 * @param {string} param.district - 区县
 * @param {string} param.detailAddress - 详细地址
 * @param {number} param.defaultState - 是否默认地址 (0:否, 1:是)
 * @param {number} param.sendType - 配送类型 (0:快递, 1:自提)
 * @param {string} param.siteAddress - 站点地址
 * @param {string} param.siteCode - 站点代码
 * @param {string} param.siteName - 站点名称
 * @param {string} param.siteType - 站点类型
 * @param {string} param.siteTypeName - 站点类型名称
 * @returns {Promise}
 */
export function addAddress(param) {
  const payload = buildAddressManagePayload(param);
  return useNuxtApp().$customFetch("/mall/admin/addressManage/add?isShop=1", {
    method: "POST",
    body: {
      param: {
        ...payload,
      },
    },
  });
}

/**
 * 修改地址
 * @param {Object} param - 地址参数对象
 * @param {string} param.addressId - 地址ID
 * @param {string} param.userRealname - 收货人姓名
 * @param {string} param.userMobile - 手机号码
 * @param {string} param.province - 省份
 * @param {string} param.city - 城市
 * @param {string} param.district - 区县
 * @param {string} param.detailAddress - 详细地址
 * @param {number} param.defaultState - 是否默认地址 (0:否, 1:是)
 * @param {number} param.sendType - 配送类型 (0:快递, 1:自提)
 * @param {string} param.siteAddress - 站点地址
 * @param {string} param.siteCode - 站点代码
 * @param {string} param.siteName - 站点名称
 * @param {string} param.siteType - 站点类型
 * @param {string} param.siteTypeName - 站点类型名称
 * @returns {Promise}
 */
export function modifyAddress(param) {
  const payload = buildAddressManagePayload(param);
  return useNuxtApp().$customFetch(
    "/mall/admin/addressManage/modify?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          ...payload,
        },
      },
    }
  );
}

/**
 * 删除地址
 * @param {Object} param - 地址参数对象
 * @param {string} param.addressId - 地址ID
 * @returns {Promise}
 */
export function deleteAddress(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/addressManage/delete?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          addressId: param.addressId || "",
        },
      },
    }
  );
}

/**
 * 创建订单
 * @param {Object} param - 订单参数对象
 * @param {Array} param.createOrderSkuReqDTOList - 订单商品列表
 * @param {string} param.addressId - 地址ID
 * @param {Array} param.giftCardActivityDetailIdList - 礼品卡活动详情ID列表
 * @param {string} param.mobileNo - 手机号码（当联系方式为手机号时）
 * @param {string} param.email - 邮箱地址（当联系方式为邮箱时）
 * @param {string} param.dealerShopId - 店铺ID（未登录用户需要）
 * @param {string} param.lineId - Line ID
 * @param {string} param.userIP - 用户 IP 地址
 * @param {string} param.ipCountry - IP 解析后的国家信息
 * @param {Object} param.ipLocation - IP 详细位置信息（包含国家、城市、经纬度、ISP等）
 * @param {string} param.domainName - 域名信息
 * @param {string} param.platformSource - 平台来源
 * @param {string} param.remark - 订单备注
 * @param {string} param.discountCode - 折扣码
 * @returns {Promise}
 */
export function createOrder(param) {
  const trackedParam = withEventTrackingFields(param || {});
  const customerAddressDTO = normalizeCustomerAddressDTO(
    trackedParam.customerAddressDTO || {}
  );
  const facebookEventCustomData = {
    currency: "TWD",
    contentIds: (trackedParam.createOrderSkuReqDTOList || [])
      .map((item) => item?.dealerItemSkuId || item?.spItemSpuId || item?.id)
      .filter(Boolean),
    contentType: "product",
    ...(trackedParam.facebookEventCustomData || {}),
  };
  const facebookConversionPayload = buildFacebookEventPayload({
    eventId: trackedParam.eventId,
    eventSourceUrl: trackedParam.eventSourceUrl,
    eventName: FACEBOOK_EVENT_NAMES.purchase,
    customData: facebookEventCustomData,
  });

  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/createOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          createOrderSkuReqDTOList: trackedParam.createOrderSkuReqDTOList || [],
          addressId: trackedParam.addressId || "",
          countryCode:
            trackedParam.countryCode || customerAddressDTO.countryCode || "",
          giftCardActivityDetailIdList:
            trackedParam.giftCardActivityDetailIdList || [],
          mobileNo: trackedParam.mobileNo || "",
          email: trackedParam.email || "",
          dealerShopId: trackedParam.dealerShopId || "",
          lineId: trackedParam.lineId || "",
          customerAddressDTO,
          userIP: trackedParam.userIP || "",
          ipCountry: trackedParam.ipCountry || "",
          ipLocation: trackedParam.ipLocation || null,
          domainName: trackedParam.domainName || "",
          platformSource: trackedParam.platformSource || "",
          remark: trackedParam.remark || "",
          discountCode: trackedParam.discountCode || "",
          userCouponIdList: Array.isArray(trackedParam.userCouponIdList)
            ? trackedParam.userCouponIdList
            : [],
          eventId: trackedParam.eventId,
          eventSourceUrl: process.client?window.location.href:trackedParam.eventSourceUrl,
          facebookConversionRequestDTO: facebookConversionPayload?.body?.param || null,
        },
      },
    }
  );
}

/**
 * 查询小程序支付订单
 * @param {Object} param - 查询参数对象
 * @param {string} param.payOrderCode - 支付订单编码
 * @param {number} param.payType - 支付类型
 * @returns {Promise}
 */
export function queryMiniProgramPayOrderByBizTradeId(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/queryMiniProgramPayOrderByBizTradeId?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          payOrderCode: param.payOrderCode || "",
          payType: param.payType || 2,
        },
      },
    }
  );
}

/**
 * 查询店铺 Conflux 支付方式
 * @param {Object} param
 * @param {string|number} param.dealerShopId - 店铺 ID
 * @returns {Promise}
 */
export function queryConfluxPaymentMethodList(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/paymentMethod/queryList?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: {
          dealerShopId: param.dealerShopId || "",
        },
      },
    }
  );
}

/**
 * 查询店铺前台配置
 * @param {Object} param
 * @param {string|number} param.shopId - 店铺 ID
 * @param {string|number} param.dealerShopId - 销售商店铺 ID
 * @returns {Promise}
 */
export function queryShopConfigByShopId(param = {}) {
  const bodyParam = {};
  if (String(param.shopId || "").trim()) {
    bodyParam.shopId = param.shopId;
  }
  if (String(param.dealerShopId || "").trim()) {
    bodyParam.dealerShopId = param.dealerShopId;
  }

  return useNuxtApp().$customFetch(
    "/mall/mall/shopConfig/queryByShopId?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param: bodyParam,
      },
    }
  );
}

/**
 * 查询会员已保存的信用卡信息（用于 Conflux 信用卡账单地址回填）
 * @param {Object|string|number} customerInfoIdOrParam - 会员 ID 或查询参数
 * @param {Object} pageParam - 分页参数
 * @returns {Promise}
 */
export function queryCustomerCreditCardInfo(customerInfoIdOrParam = {}, pageParam = {}) {
  const param = typeof customerInfoIdOrParam === "object" && customerInfoIdOrParam !== null
    ? customerInfoIdOrParam
    : {
        ...pageParam,
        customerInfoId: customerInfoIdOrParam,
      };
  const authorization = process.client
    ? getToken() || getMallAppToken() || ""
    : (() => {
        try {
          const headers = useRequestHeaders(["authorization"]);
          return headers?.authorization || "";
        } catch (error) {
          return "";
        }
      })();

  return useNuxtApp().$customFetch(
    `/mall/admin/customerInfo/queryCustomerCreditCardInfo?customerInfoId=${encodeURIComponent(param.customerInfoId || "")}&uncheck401=1`,
    {
      method: "POST",
      headers: {
        authorization,
      },
      body: {
        param: {
          pageNo: param.pageNo ?? 0,
          pageSize: param.pageSize ?? 10,
        },
      },
    }
  );
}

/**
 * 创建 Conflux 支付订单
 * @param {Object} param - Conflux 支付参数
 * @returns {Promise}
 */
export function createConfluxPaymentOrder(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/paymentOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: {
        param,
      },
    }
  );
}

/**
 * 获取地址树数据（省份、城市、区县）
 * @param {Object} param - 查询参数对象
 * @param {number} param.param - 地址级别参数，4表示获取完整地址树
 * @returns {Promise}
 */
export function getAddressTree(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/portal/addressLibrary/getAddressTree?isShop=1",
    {
      method: "POST",
      body: {
        param: param.param || 4,
      },
      headers: {
        Accept: "*/*",
        "Accept-Language": "zh-CN,zh;q=0.9",
        "Content-Type": "application/json;charset=UTF-8",
        Origin: "http://mallfront.mps.omszt.com:7001",
        "Proxy-Connection": "keep-alive",
        Referer: "http://mallfront.mps.omszt.com:7001/",
        "User-Agent":
          "Mozilla/5.0 (Linux; Android 6.0; Nexus 5 Build/MRA58N) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/125.0.0.0 Mobile Safari/537.36",
      },
    }
  );
}

/**
 * 获取7-11自提点地址列表
 * @param {Object} param - 查询参数对象
 * @param {string} param.country - 国家/地区
 * @param {string} param.pickupPointMode - 自提点模式 (1: 7-11)
 * @param {string} param.province - 省份
 * @param {string} param.city - 城市
 * @param {string} param.district - 区县
 * @param {string} param.siteName - 店铺名称（关键字搜索）
 * @param {string} param.detailAddress - 详细地址
 * @param {string} param.dealerShopId - 店铺ID
 * @param {boolean} param.includeIsGroup - 是否传isGroup(默认true，搜索时可关闭)
 * @param {number} param.pageNo - 页码，从0开始
 * @param {number} param.pageSize - 每页大小
 * @returns {Promise}
 */
export function get711AddressList(param = {}) {
  const includeIsGroup = param.includeIsGroup !== false
 
  return useNuxtApp().$customFetch(
    "/mall/admin/addressManage/dealer-shop-zt-address/pageList?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          country: param.country || "中國臺灣",
          dealerShopId:    (process.client ?useCookie('dealerShopId').value : ""),
          pickupPointMode: param.pickupPointMode || "1",
          province: param.province || "",
          // dealerShopId: param.dealerShopId || "",
          district: param.district || "",
          city: param.city || "",
          siteName: param.siteName || "",
          detailAddress: param.detailAddress || "",
          ...(includeIsGroup ? { isGroup: 1 } : {}),
          street: param.street || "",
          pageNo: param.pageNo || 0,
          pageSize: 10000
        },
      },

      // {
      //   method: "POST",
      //   body: {
      //     param: {
      //       country: param.country || "中國臺灣",
      //       pickupPointMode: param.pickupPointMode || "1",
      //       province: param.province || "",
      //       dealerShopId: param.dealerShopId || "",
      //       district: param.district || "",
      //       city: param.city || "",
      //       detailAddress: param.detailAddress || "",
      //       pageNo: param.pageNo || 0,
      //       pageSize: param.pageSize || 20
      //     }
      //   },
      // headers: {
      //   'Accept': '*/*',
      //   'Accept-Language': 'zh-CN,zh-TW;q=0.9,zh;q=0.8,ja;q=0.7',
      //   'Cache-Control': 'no-cache',
      //   'Connection': 'keep-alive',
      //   'Content-Type': 'application/json;charset=UTF-8',
      //   'Origin': 'https://runpiggy.com',
      //   'Pragma': 'no-cache',
      //   'Referer': 'https://runpiggy.com/h5/pages/userPages/address/CourierPickupPointList',
      //   'Sec-Fetch-Dest': 'empty',
      //   'Sec-Fetch-Mode': 'cors',
      //   'Sec-Fetch-Site': 'same-origin',
      //   'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 18_5 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.5 Mobile/15E148 Safari/604.1'
      // }
    }
  );
}

export function queryMallOrderList(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/queryMallOrderList?isShop=1",
    {
      method: "POST",
      body: {
        param,
      },
    }
  );
}
/**
 * 获取商品评论列表
 * @param {Object} param - 查询参数对象
 * @param {string} param.dealerItemSpuId - 商品SPU ID
 * @param {number} param.commentType - 评论类型 (1:商品评论)
 * @param {Array} param.commentStateList - 评论状态列表 [1,2] (1:已审核, 2:待审核)
 * @param {number} param.haveImg - 是否有图片 (0:全部, 1:有图片)
 * @param {number} param.haveVideo - 是否有视频 (0:全部, 1:有视频)
 * @param {number} param.addCommentState - 是否有追评 (0:全部, 1:有追评, 2:无追评)
 * @param {number} param.pageNo - 页码，从0开始
 * @param {number} param.pageSize - 每页大小
 * @returns {Promise}
 */
export function getItemCommentBySpuId(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallItemComment/getItemCommentBySpuId?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          dealerShopId: param.dealerShopId || "",
          dealerItemSpuId: param.dealerItemSpuId || "",
          commentType: param.commentType || 1,
          commentStateList: param.commentStateList || [1, 2],
          haveImg: param.haveImg !== undefined ? param.haveImg : undefined,
          haveVideo: param.haveVideo !== undefined ? param.haveVideo : undefined,
          addCommentState: param.addCommentState !== undefined ? param.addCommentState : undefined,
          pageNo: param.pageNo || 0,
          pageSize: param.pageSize || 20
        }
      },
      headers: {
        'Accept': '*/*',
        'Accept-Language': 'zh-CN,zh;q=0.9',
        'Connection': 'keep-alive',
        'Content-Type': 'application/json;charset=UTF-8',
        'Origin': 'http://mallfront.mps.omszt.com:7001',
        'Referer': 'http://mallfront.mps.omszt.com:7001/',
        'User-Agent': 'Mozilla/5.0 (Linux; Android 6.0; Nexus 5 Build/MRA58N) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/125.0.0.0 Mobile Safari/537.36'
      }
    }
  );
}

/**
 * 查询商品评论分页列表
 * 对应接口：/mall/admin/dealerItemSpuReview/queryDealerItemSpuReviewPage
 * @param {Object} param
 * @param {string|number} param.dealerShopId - 店铺ID
 * @param {string|number} param.dealerItemSpuId - 商品SPU ID
 * @param {number} [param.pageNo=0] - 页码
 * @param {number} param.pageSize - 每页大小
 * @returns {Promise}
 */
export function getProductReviewPage(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/dealerItemSpuReview/queryDealerItemSpuReviewPage",
    {
      method: "POST",
      body: {
        param: {
          dealerShopId: param.dealerShopId || "",
          dealerItemSpuId: param.dealerItemSpuId || "",
          pageNo: param.pageNo != null ? param.pageNo : 0,
          pageSize: param.pageSize || 20,
        },
      },
    }
  );
}

/**
 * 获取店铺评论列表
 * 对应接口：/mall/admin/dealerItemSpuReview/queryShopReviewPage
 * @param {Object} param
 * @param {string|number} param.dealerShopId - 店铺ID
 * @param {number} [param.pageNo=0] - 页码
 * @param {number} param.pageSize - 每页大小
 * @returns {Promise}
 */
export function getShopReviewPage(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/dealerItemSpuReview/queryShopReviewPage?getCache=1",
    {
      method: "POST",
      body: {
        param: {
          dealerShopId: param.dealerShopId || "",
          pageNo: param.pageNo != null ? param.pageNo : 0,
          pageSize: param.pageSize || 20,
        },
      },
    }
  );
}

/**
 * 添加评论（商品评论和店铺评论公用）
 * 对应接口：/mall/admin/dealerItemSpuReview/addDealerItemSpuReview
 * @param {Object} param
 * @param {number} param.type - 评论类型 0=商品评论, 1=店铺评论
 * @param {string|number} [param.dealerItemSpuId] - 商品SPU ID（商品评论必填）
 * @param {string|number} [param.dealerShopId] - 店铺ID
 * @param {string|number} [param.shopId] - 店铺ID (deprecated)
 * @param {string|number} [param.spInfoId] - SP信息ID
 * @param {string|number} [param.dealerInfoId] - 经销商ID
 * @param {number} param.score - 评分 1-5
 * @param {string} param.commentContent - 评论内容
 * @param {string} [param.reviewContent] - 回复内容
 * @param {Array<string>} [param.commentImgUrlList] - 图片URL列表
 * @param {Array<string>} [param.commentVideoUrlList] - 视频URL列表
 * @param {string} param.name - 用户名
 * @param {string} param.email - 用户邮箱
 * @returns {Promise}
 */
export function addReview(param = {}) {
  console.log('addReview API called with params:', {
    type: param.type,
    dealerItemSpuId: param.dealerItemSpuId,
    dealerShopId: param.dealerShopId,
    shopId: param.shopId,
    spInfoId: param.spInfoId,
    dealerInfoId: param.dealerInfoId,
  })
  
  return useNuxtApp().$customFetch(
    "/mall/admin/dealerItemSpuReview/addDealerItemSpuReview",
    {
      method: "POST",
      body: {
        param: {
          type: param.type,
          dealerItemSpuId: param.dealerItemSpuId,
          dealerShopId: param.dealerShopId,
          shopId: param.shopId,
          spInfoId: param.spInfoId,
          dealerInfoId: param.dealerInfoId,
          score: param.score || 5,
          commentContent: param.commentContent || "",
          reviewContent: param.reviewContent || "",
          commentImgUrlList: param.commentImgUrlList || [],
          commentVideoUrlList: param.commentVideoUrlList || [],
          name: param.name || "",
          email: param.email || "",
        },
      },
    }
  );
}


/**
 * 根据订单编码查询评论列表
 * 对应接口：/mall/admin/mallItemComment/getItemCommentList
 * @param {Object} param
 * @param {string} param.orderCode - 订单编码
 * @param {number} [param.commentType=1] - 评论类型
 * @param {number} [param.commentState=1] - 评论状态
 * @param {string|number} [param.dealerShopId] - 店铺ID（可从 cookie 读取）
 */
export function getItemCommentList(param = {}) {
  const bodyParam = {
    orderCode: param.orderCode || "",
    commentType: param.commentType != null ? param.commentType : 1,
    commentState: param.commentState != null ? param.commentState : 1,
    dealerShopId: param.dealerShopId,
  };

  // 若未显式传入 dealerShopId，尝试从 cookie 读取（仅客户端）
  if (!bodyParam.dealerShopId) {
    try {
      const dealerShopIdCookie = process.client ? useCookie("dealerShopId", { path: "/" }) : null;
      if (dealerShopIdCookie && dealerShopIdCookie.value) {
        bodyParam.dealerShopId = dealerShopIdCookie.value;
      }
    } catch (_) {}
  }

  return useNuxtApp().$customFetch(
    "/mall/admin/mallItemComment/getItemCommentList?isShop=1",
    {
      method: "POST",
      body: { param: bodyParam },
      headers: {
        Accept: "application/json",
        "Accept-Language": "zh-CN,zh;q=0.9,en;q=0.8",
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );
}

/**
 * 获取订单详情
 * @param {Object} param - 参数对象
 * @param {string} param.id - 订单ID
 * @param {string} param.orderCode - 订单编码
 * @returns {Promise}
 */
export function queryMallOrderDetail(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/queryMallOrderDetail?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          id: param.id || "",
          orderCode: param.orderCode || "",
        },
      },
    }
  );
}

// /**
//  * 查询订单详情 - 用于订单详情页面
//  * @param {Object} param - 参数对象
//  * @param {string} param.id - 订单ID
//  * @param {string} param.orderCode - 订单编码
//  * @returns {Promise}
//  */
// export function querySelectionAppDealerOrderDetail(param) {
//   console.log(param,"param")
//   return useNuxtApp().$customFetch(
//     "/mall/admin/mallOrder/querySelectionAppDealerOrderDetail?isShop=1",
//     {
//       method: "POST",
//       body: {
//         param: {
//           id: '1192486561249447936'         },
//       },
//     }
//   );
// }

export function querySelectionAppDealerOrderDetail(params, $fetch, headers) {  
  const body = {
    param: {
      orderCode: params?.orderCode || params?.param?.orderCode || ""
    }
  };
  
  console.log(body, "body");

  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/mallOrder/querySelectionAppDealerOrderDetail?isShop=1",
      {
        method: "POST",
        body,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/mallOrder/querySelectionAppDealerOrderDetail?isShop=1",
      {
        method: "POST",
        body,
      }
    );
  }
}

/**
 * 取消订单
 * @param {Object} param - 参数对象
 * @param {string} param.dealerOrderPrimaryId - 订单ID
 * @returns {Promise}
 */
export function cancelOrder(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/cancelOrder?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          dealerOrderPrimaryId: param.dealerOrderPrimaryId,
        },
      },
    }
  );
}

/**
 * 申请退款
 * @param {Object} param - 参数对象
 * @param {string} param.dealerOrderPrimaryId - 订单ID
 * @returns {Promise}
 */
export function refundOrder(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/orderRefund/createOrderRefund?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          dealerOrderPrimaryId: param.dealerOrderPrimaryId,
        },
      },
    }
  );
}

/**
 * 确认收货
 * @param {Object} param - 参数对象
 * @param {string} param.dealerOrderPrimaryId - 订单ID
 * @returns {Promise}
 */
export function confirmReceive(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/confirmReceive?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          dealerOrderPrimaryId: param.dealerOrderPrimaryId,
        },
      },
    }
  );
}

/**
 * 支付订单
 * @param {Object} param - 参数对象
 * @param {Array} param.mallOrderDTOList - 订单列表
 * @param {string} param.orderId - 订单ID
 * @param {string} param.version - 版本号
 * @returns {Promise}
 */
export function payOrder(param) {
  return useNuxtApp().$customFetch("/mall/admin/mallOrder/payOrder?isShop=1", {
    method: "POST",
    body: {
      param: {
        mallOrderDTOList: param.mallOrderDTOList || [],
        orderId: param.orderId || "",
        version: param.version || "",
      },
    },
  });
}

//查看我的各种状态订单个数
export function querySelectionAppDealerOrderSummary(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/queryMallOrderSummary?isShop=1",
    {
      method: "POST",
      body: param,
    }
  );
}

export function addSpItem2Dealer(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/addSpItem2Dealer?isShop=1",
    {
      method: "POST",
      body: param,
    }
  );
}
  
// 合并支付
export function paymentOrder(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/paymentOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: param,
    }
  );
}

// 单个支付
export function paymentSingleOrder(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/paymentSingleOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: param,
    }
  );
}

// 单个支付
export function unpaymentOrder(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/unpaymentOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: param,
    }
  );
}

// 单个支付
export function unpaymentSingleOrder(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/unpaymentSingleOrder?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: param,
    }
  );
}

// 合并支付
export function getStoreConfig(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/getStoreConfig?isShop=1",
    {
      method: "POST",
      body: param,
    }
  );
}

/**
 * 校验折扣碼
 * @param {Object} param - 参数对象
 * @param {Array} param.confirmOrderSkuReqDTOList - 订单商品列表（每个成员需包含 discountCode 和 dealerShopId）
 * @returns {Promise}
 */
export function validateDiscountCode(param) {
  return useNuxtApp().$customFetch(
    "/mall/admin/discount/validateDiscountCode?isShop=1",
    {
      method: "POST",
      body: {
        param: {
          confirmOrderSkuReqDTOList: param.confirmOrderSkuReqDTOList
        }
      }
    }
  );
}

 
 
 
/**
 * 新增商品评论
 * 使用接口：/admin/mallItemComment/addItemAddComment
 * 为保持与现有接口风格一致，前缀沿用 /mall/admin，并追加 isShop=1
 * @param {Object} param - 评论参数对象，原样透传至后端
 * @returns {Promise}
 */
export function addItemAddComment(param = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/mallItemComment/addItemCommentList?isShop=1",
    {
      method: "POST",
      body: {
        param: [{
          ...param
        }]
      }
    }
  );
}

/**
 * 根据订单查询评论并返回首条评论ID
 * 对应后端：/api/cmsadmin/mall/admin/mallItemComment/getItemCommentList?isShop=1
 * @param {Object} param
 * @param {string} param.orderCode - 订单编码
 * @param {number} [param.commentType=1]
 * @param {number} [param.commentState=1]
 * @param {string|number} [param.dealerShopId]
 * @returns {Promise<string|undefined>} 首条评论ID
 */
export function getItemCommentFirstId(param = {}) {
  const bodyParam = {
    orderCode: param.orderCode || "",
    commentType: param.commentType != null ? param.commentType : 1,
    commentState: param.commentState != null ? param.commentState : 1,
    dealerShopId: param.dealerShopId,
  };

  if (!bodyParam.dealerShopId) {
    try {
      const dealerShopIdCookie = process.client ? useCookie("dealerShopId", { path: "/" }) : null;
      if (dealerShopIdCookie && dealerShopIdCookie.value) {
        bodyParam.dealerShopId = dealerShopIdCookie.value;
      }
    } catch (_) {}
  }

  return useNuxtApp()
    .$customFetch(
      "/mall/admin/mallItemComment/getItemCommentList?isShop=1",
      {
        method: "POST",
        body: { param: bodyParam },
        headers: {
          Accept: "application/json",
          "Accept-Language": "zh-CN,zh;q=0.9,en;q=0.8",
          "Content-Type": "application/json;charset=UTF-8",
        },
      }
    )
    .then((res) => {
      try {
        return res?.data?.dataList?.[0]?.id;
      } catch (_) {
        return undefined;
      }
    });
}

/**
 * 获取阿里云 OSS 上传 token
 * @returns {Promise}
 */
export function getAliyunToken() {
  
  return useNuxtApp().$customFetch(
    "/mall/basic/aliyun/getToken?isShop=1",
    {
      method: "GET"
    }
  );
}

/**
 * 查询订单列表（不需要登录）- C端订单查询
 * @param {Object} param - 查询参数对象
 * @param {string} param.mobileNo - 用户手机号
 * @param {string} param.email - 用户邮箱
 * @param {string} param.nickname - 用户昵称/名字
 * @param {string} param.account - 用户账号
 * @param {string} param.lineId - LineId
 * @param {string} param.orderCode - 订单号
 * @param {string} param.dealerShopId - 店铺ID
 * @returns {Promise}
 */
export function queryMallOrderListNoLogin(param = {}) {
  const bodyParam = {
    param: {
      mobileNo: param.mobileNo !== undefined ? param.mobileNo : null,
      email: param.email !== undefined ? param.email : null,
      nickname: param.nickname !== undefined ? param.nickname : null,
      account: param.account !== undefined ? param.account : null,
      lineId: param.lineId !== undefined ? param.lineId : null,
      orderCode: param.orderCode !== undefined ? param.orderCode : null,
      payOrderCode: param.payOrderCode !== undefined ? param.payOrderCode : null
    }
  }

  // 获取 dealerShopId
  if (!bodyParam.dealerShopId) {
    if (param.dealerShopId) {
      bodyParam.param.dealerShopId = param.dealerShopId
    } else {
      try {
        const dealerShopIdCookie = process.client ? useCookie("dealerShopId", { path: "/" }) : null
        if (dealerShopIdCookie && dealerShopIdCookie.value) {
          bodyParam.param.dealerShopId = dealerShopIdCookie.value
        }
      } catch (e) {
        // no-op
      }
    }
  }

  return useNuxtApp().$customFetch(
    "/mall/admin/mallOrder/queryMallOrderListWithSpAndOrderCodeGroupNoLogin?isShop=1&uncheck401=1",
    {
      method: "POST",
      body: bodyParam
    }
  );
}
